import { useState, useEffect } from 'react';
import { api } from '../api';
import { styles } from '../styles';
import { BackIcon } from '../components/Icons';

export default function ReceiptEditScreen({ receipt, onBack, onSaved }) {
  const [merchant, setMerchant] = useState(receipt.merchant_name || '');
  const [amount, setAmount] = useState(receipt.grand_total?.toString() || '');
  const [categoryId, setCategoryId] = useState(receipt.category?.id || '');
  const [expenseType, setExpenseType] = useState(receipt.expense_type || 'personal');
  const [categories, setCategories] = useState([]);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState(null);
  const [fullDetail, setFullDetail] = useState(null);
  const [imageModalOpen, setImageModalOpen] = useState(false);

  // Split state
  const [isSplit, setIsSplit] = useState(false);
  const [splits, setSplits] = useState([{ categoryId: '', amount: '' }]);

  useEffect(() => {
    api.get('/categories').then(setCategories).catch(() => {});
    api.get(`/receipts/${receipt.id}`).then(detail => {
      setFullDetail(detail);
      // If receipt has splits, populate the split state
      if (detail.splits && detail.splits.length > 0) {
        setIsSplit(true);
        // Primary category is the largest (first in list)
        setCategoryId(detail.splits[0].category_id);
        // Remaining splits (skip the first one which is primary)
        if (detail.splits.length > 1) {
          setSplits(detail.splits.slice(1).map(s => ({
            categoryId: s.category_id,
            amount: s.amount.toString(),
          })));
        }
      }
    }).catch(() => {});
  }, [receipt.id]);

  const imageUrl = fullDetail?.image_url;
  const isPdf = imageUrl?.toLowerCase().endsWith('.pdf');

  const splitsTotal = splits.reduce((sum, s) => sum + (parseFloat(s.amount) || 0), 0);
  const grandTotal = parseFloat(amount) || 0;
  const remainder = grandTotal - splitsTotal;

  const addSplit = () => {
    setSplits([...splits, { categoryId: '', amount: '' }]);
  };

  const removeSplit = (index) => {
    if (splits.length > 1) {
      setSplits(splits.filter((_, i) => i !== index));
    }
  };

  const updateSplit = (index, field, value) => {
    const updated = [...splits];
    updated[index][field] = value;
    setSplits(updated);
  };

  const handleSave = async () => {
    if (!merchant || !amount || !categoryId) {
      setError('Please fill in all required fields');
      return;
    }

    if (isSplit) {
      const invalidSplits = splits.some(s => !s.categoryId || !s.amount);
      if (invalidSplits) {
        setError('Please fill in all split fields');
        return;
      }
      if (splitsTotal >= grandTotal) {
        setError('Splits must be less than the total amount');
        return;
      }
      if (remainder <= 0) {
        setError('Nothing left for primary category');
        return;
      }
    }

    setSaving(true);
    setError(null);

    try {
      const payload = {
        merchant_name: merchant,
        grand_total: parseFloat(amount),
        category_id: categoryId,
        expense_type: expenseType,
      };

      if (isSplit) {
        payload.splits = splits.map(s => ({
          category_id: s.categoryId,
          amount: parseFloat(s.amount),
        }));
      }

      await api.patch(`/receipts/${receipt.id}`, payload);
      onSaved();
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('Delete this receipt? This cannot be undone.')) {
      return;
    }
    
    setDeleting(true);
    try {
      await api.delete(`/receipts/${receipt.id}`);
      onSaved();
    } catch (e) {
      setError('Failed to delete');
      setDeleting(false);
    }
  };

  const selectedCategory = categories.find(c => c.id === categoryId);

  return (
    <div style={styles.screen}>
      <header style={styles.header}>
        <button style={styles.backBtn} onClick={onBack}>
          <BackIcon />
        </button>
        <h1 style={styles.headerTitle}>Edit Receipt</h1>
        <div style={{ width: 20 }} />
      </header>

      {imageUrl && (
        <button 
          style={editStyles.imageContainer}
          onClick={() => setImageModalOpen(true)}
        >
          <img 
            src={imageUrl} 
            alt="Receipt" 
            style={editStyles.image}
          />
          <div style={editStyles.tapHint}>Tap to view</div>
        </button>
      )}

      <div style={styles.form}>
        <div style={styles.field}>
          <label style={styles.label}>Merchant</label>
          <input
            style={styles.input}
            type="text"
            placeholder="Where did you spend?"
            value={merchant}
            onChange={(e) => setMerchant(e.target.value)}
          />
        </div>

        <div style={styles.field}>
          <label style={styles.label}>Amount</label>
          <div style={styles.amountInput}>
            <span style={styles.currency}>$</span>
            <input
              style={{...styles.input, paddingLeft: 28}}
              type="number"
              inputMode="decimal"
              placeholder="0.00"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
            />
          </div>
        </div>

        <div style={styles.field}>
          <label style={styles.label}>Category</label>
          <select
            style={styles.select}
            value={categoryId}
            onChange={(e) => setCategoryId(e.target.value)}
          >
            <option value="">Select category</option>
            {categories.filter(c => c.is_active && c.parent_id === null && c.budget_type !== 'project').map(c => (
              <option key={c.id} value={c.id}>{c.icon} {c.name}</option>
            ))}
            {categories.filter(c => c.is_active && c.parent_id === null && c.budget_type === 'project').map(proj => {
              const subs = categories.filter(c => c.parent_id === proj.id && c.is_active);
              if (!subs.length) return null;
              return (
                <optgroup key={proj.id} label={`${proj.icon || ''} ${proj.name}`}>
                  {subs.map(c => (
                    <option key={c.id} value={c.id}>{c.icon} {c.name}</option>
                  ))}
                </optgroup>
              );
            })}
          </select>
        </div>

        <div style={styles.field}>
          <label style={styles.label}>Type</label>
          <div style={styles.segmentedControl}>
            <button
              style={{
                ...styles.segment,
                ...(expenseType === 'personal' ? styles.segmentActive : {})
              }}
              onClick={() => setExpenseType('personal')}
            >
              Personal
            </button>
            <button
              style={{
                ...styles.segment,
                ...(expenseType === 'corporate' ? styles.segmentActive : {})
              }}
              onClick={() => setExpenseType('corporate')}
            >
              Corporate
            </button>
          </div>
        </div>

        {/* Split toggle */}
        <div style={{ ...styles.field, flexDirection: 'row', alignItems: 'center', gap: 10 }}>
          <input
            type="checkbox"
            id="splitToggle"
            checked={isSplit}
            onChange={(e) => setIsSplit(e.target.checked)}
          />
          <label htmlFor="splitToggle" style={{ ...styles.label, marginBottom: 0 }}>
            Split across categories
          </label>
        </div>

        {isSplit && (
          <div style={styles.field}>
            <label style={styles.label}>Split portions</label>
            
            {splits.map((split, idx) => (
              <div key={idx} style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
                <select
                  style={{ ...styles.input, flex: 2 }}
                  value={split.categoryId}
                  onChange={(e) => updateSplit(idx, 'categoryId', e.target.value)}
                >
                  <option value="">Category...</option>
                  {categories.filter(c => c.is_active && c.id !== categoryId).map((c) => (
                    <option key={c.id} value={c.id}>{c.icon} {c.name}</option>
                  ))}
                </select>
                <input
                  style={{ ...styles.input, flex: 1 }}
                  type="number"
                  step="0.01"
                  placeholder="0.00"
                  value={split.amount}
                  onChange={(e) => updateSplit(idx, 'amount', e.target.value)}
                />
                {splits.length > 1 && (
                  <button
                    style={{ ...styles.backBtn, color: '#ef4444' }}
                    onClick={() => removeSplit(idx)}
                  >
                    ✕
                  </button>
                )}
              </div>
            ))}
            
            <button
              style={{ ...styles.secondaryBtn, marginTop: 4 }}
              onClick={addSplit}
            >
              + Add another split
            </button>

            {selectedCategory && amount && (
              <div style={{ 
                marginTop: 12, 
                padding: 12, 
                backgroundColor: '#f0fdf4', 
                borderRadius: 8,
                border: '1px solid #bbf7d0'
              }}>
                <span style={{ color: '#166534', fontSize: 14 }}>
                  {selectedCategory.icon} {selectedCategory.name}: <strong>${remainder.toFixed(2)}</strong> (remainder)
                </span>
              </div>
            )}
          </div>
        )}

        {error && <p style={styles.error}>{error}</p>}

        <button 
          style={styles.primaryBtn} 
          onClick={handleSave} 
          disabled={saving}
        >
          {saving ? 'Saving...' : 'Save Changes'}
        </button>

        <button 
          style={editStyles.deleteBtn} 
          onClick={handleDelete}
          disabled={deleting}
        >
          {deleting ? 'Deleting...' : 'Delete Receipt'}
        </button>
      </div>

      {/* Fullscreen Image/PDF Modal */}
      {imageModalOpen && imageUrl && (
        <div 
          style={editStyles.modal}
          onClick={() => setImageModalOpen(false)}
        >
          <button 
            style={editStyles.closeBtn}
            onClick={() => setImageModalOpen(false)}
          >
            ✕
          </button>
          {isPdf ? (
            <iframe
              src={imageUrl}
              style={editStyles.pdfViewer}
              title="Receipt PDF"
            />
          ) : (
            <img 
              src={imageUrl} 
              alt="Receipt full" 
              style={editStyles.modalImage}
              onClick={(e) => e.stopPropagation()}
            />
          )}
        </div>
      )}
    </div>
  );
}

const editStyles = {
  imageContainer: {
    margin: '0 16px 16px',
    borderRadius: 12,
    overflow: 'hidden',
    border: '1px solid #e2e8f0',
    cursor: 'pointer',
    position: 'relative',
    background: 'none',
    padding: 0,
  },
  image: {
    width: '100%',
    maxHeight: 200,
    objectFit: 'cover',
  },
  tapHint: {
    position: 'absolute',
    bottom: 8,
    right: 8,
    backgroundColor: 'rgba(0,0,0,0.6)',
    color: 'white',
    fontSize: 12,
    padding: '4px 8px',
    borderRadius: 4,
  },
  deleteBtn: {
    marginTop: 12,
    padding: '14px 20px',
    backgroundColor: 'white',
    color: '#ef4444',
    border: '1px solid #fecaca',
    borderRadius: 12,
    fontSize: 16,
    fontWeight: 500,
    cursor: 'pointer',
    width: '100%',
  },
  modal: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0,0,0,0.9)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000,
  },
  modalImage: {
    maxWidth: '95%',
    maxHeight: '95%',
    objectFit: 'contain',
  },
  pdfViewer: {
    width: '95%',
    height: '95%',
    border: 'none',
    backgroundColor: 'white',
  },
  closeBtn: {
    position: 'absolute',
    top: 16,
    right: 16,
    width: 40,
    height: 40,
    borderRadius: 20,
    backgroundColor: 'rgba(255,255,255,0.2)',
    color: 'white',
    border: 'none',
    fontSize: 20,
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
};